/**
 * Shared helpers for "save as image" / "save image to loan" on map and street-view
 * previews. Both PropertyMap modes (aerial + street) pull from the Maps Static
 * and Street View Static APIs, which return plain JPEGs, so we can fetch the
 * URL as a blob and either download it or wrap it in a File for upload.
 */

const mimeToExt = (mime) => {
  if (!mime) return "jpg";
  if (mime.includes("png")) return "png";
  if (mime.includes("webp")) return "webp";
  return "jpg";
};

export const downloadImageUrl = async (url, suggestedName = "map-image") => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch image (HTTP ${response.status})`);
  }
  const blob = await response.blob();
  const ext = mimeToExt(blob.type);
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = `${suggestedName}.${ext}`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
};

/** Strip filesystem-unsafe chars so a friendly display name can safely be
 *  used as both the browser download filename and the loan file's `name`. */
const sanitizeFilename = (name) =>
  String(name || "map-image").replace(/[\\/:*?"<>|]+/g, "-").trim();

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/** Short date string for filenames: "Apr 20 2026". Avoids commas/slashes
 *  that muddle filesystem paths or URL-encode oddly. */
const formatDateForFilename = (date = new Date()) =>
  `${MONTHS[date.getMonth()]} ${String(date.getDate()).padStart(2, "0")} ${date.getFullYear()}`;

/** Compact time suffix for id uniqueness within the same day: "143005". */
const formatTimeForId = (date = new Date()) =>
  `${String(date.getHours()).padStart(2, "0")}${String(date.getMinutes()).padStart(2, "0")}${String(date.getSeconds()).padStart(2, "0")}`;

/**
 * Fetch a remote image URL and wrap it in a File payload ready for
 * useLoanFilesHook().uploadLoanDocs() — matches the uploadMetadata shape
 * that useAxiosHook expects (see useAxiosHook.js: it pulls `.file` off each
 * entry and posts as `uploadFiles`, serializing the rest as `submission`).
 *
 * `displayName` is what the user sees (no timestamp, no extension). The
 * internal `id` gets a timestamp suffix to stay unique across re-uploads.
 */
export const buildLoanUploadPayloadFromUrl = async ({
  url,
  displayName = "map-image",
  docGroup = "Property Documents",
  docType = null,
}) => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch image (HTTP ${response.status})`);
  }
  const blob = await response.blob();
  const ext = mimeToExt(blob.type);
  const now = new Date();
  const cleanName = sanitizeFilename(
    `${displayName} - ${formatDateForFilename(now)}`,
  );
  const fileName = `${cleanName}.${ext}`;
  const file = new File([blob], fileName, {
    type: blob.type || "image/jpeg",
  });
  return [
    {
      file,
      // useAxiosHook passes `id` as the multipart filename (3rd arg to
      // formDataPayload.append) — so whatever ends up here is what the
      // backend persists as the uploaded file's name. Keep it readable.
      id: `${cleanName}-${formatTimeForId(now)}.${ext}`,
      name: fileName,
      docGroup,
      docType,
    },
  ];
};
